import { z } from 'zod'

// ===== POST /createSpreadsheet =====

/**
 * Most rows one create may carry. V1 sends at most every row Plaid holds for the
 * chosen accounts — a 90-day window at link time plus whatever has accrued since — so
 * 25,000 is about 34 a day across two full years, well past any real export,
 * while still bounding what one request can make the server hold and process.
 */
export const MAX_SHEET_TRANSACTIONS = 25_000

export const CreateSpreadsheetRequestSchema = z.object({
  operationId: z.string().uuid().optional(),
  sheetSelection: z.enum(['new', 'existing', 'template']), // Whether to create a new sheet, use an existing one, or use the BankSheets template
  exportType: z.enum(['monthly', 'accounts', 'single']), // Layout style: monthly tabs, per-account tabs, or single tab
  transactions: z.array(z.record(z.unknown())).max(MAX_SHEET_TRANSACTIONS), // Raw transaction objects from Plaid
  categorizeTransactions: z.boolean().optional(), // Categorize after selected rows have been filtered
  spreadsheetTitle: z.string().optional(), // Title for the new spreadsheet (sheetSelection='new')
  existingSpreadsheetId: z.string().optional(), // Google Sheets ID to write into (sheetSelection='existing')
  existingSheetTab: z.string().optional(), // Tab name within the existing spreadsheet
  folderId: z.string().optional(), // Google Drive folder ID to place the new spreadsheet in
  selectedAccountIds: z.array(z.string()).optional(), // Filter transactions to these Plaid account IDs
  /**
   * The same filter, qualified by the bank each account belongs to.
   *
   * Sent alongside selectedAccountIds rather than replacing it: V0 and any
   * in-flight client still send the bare list, and both must keep working. When
   * this is present it wins, because an account id on its own cannot say which
   * bank it belongs to.
   */
  selectedAccounts: z.array(z.object({ itemId: z.string(), accountId: z.string() })).optional(),
  enableAutomaticSyncing: z.boolean().optional(), // Whether to schedule recurring sync for this sheet
  syncFrequency: z.string().optional(), // Cron-style or named frequency (e.g. 'daily', 'weekly')
  templateId: z.string().optional(), // Sheet template ID to apply on creation
})

export const CreateSpreadsheetResponseSchema = z.object({
  spreadsheetId: z.string(), // Google Sheets file ID of the created/updated spreadsheet
  spreadsheetUrl: z.string(), // Full Google Sheets URL to open in browser
  warnings: z.array(z.string()).optional(), // Non-fatal limitations encountered while writing rows
})

// ===== POST /appendTransactions =====

export const AppendTransactionsRequestSchema = z.object({
  spreadsheetId: z.string(), // Google Sheets file ID to append into
  transactions: z.array(z.record(z.unknown())).min(1), // At least one transaction required
  exportType: z.enum(['monthly', 'accounts', 'single']).optional().default('single'), // Tab layout; defaults to 'single'
  lastSyncedTabs: z.array(z.string()).optional(), // Tab names already synced — used to avoid duplicate writes
})

export const AppendTransactionsResponseSchema = z.object({
  message: z.string(), // Success confirmation message
})

// ===== GET /listSpreadsheets =====

export const SpreadsheetItemSchema = z.object({
  id: z.string(), // Google Drive file ID
  title: z.string(), // Spreadsheet display name
  modifiedAt: z.string().optional(), // Last modified date as localeDateString (from Drive API)
})

export const ListSpreadsheetsResponseSchema = z.object({
  spreadsheets: z.array(SpreadsheetItemSchema), // All spreadsheets found in the user's Google Drive
})

// ===== GET /templatePreview =====

export const TemplatePreviewResponseSchema = z.object({
  // Null when BANKSHEETS_TEMPLATE_ID is unset on this deployment. The preview page
  // falls back to the screenshot alone rather than offering a link that would open
  // a Drive 404, so a missing env var degrades the page instead of breaking it.
  url: z.string().url().nullable(),
})

// Derived TypeScript types
export type CreateSpreadsheetRequest = z.infer<typeof CreateSpreadsheetRequestSchema>
export type CreateSpreadsheetResponse = z.infer<typeof CreateSpreadsheetResponseSchema>
export type AppendTransactionsRequest = z.infer<typeof AppendTransactionsRequestSchema>
export type AppendTransactionsResponse = z.infer<typeof AppendTransactionsResponseSchema>
export type SpreadsheetItem = z.infer<typeof SpreadsheetItemSchema>
export type ListSpreadsheetsResponse = z.infer<typeof ListSpreadsheetsResponseSchema>
export type TemplatePreviewResponse = z.infer<typeof TemplatePreviewResponseSchema>
